// Korea's reward machine (B16; default pick K8-MJ78-11, docs/renders/targets/run-2026-10-14/
// claw-r8-sheet.png): a purple claw machine with a clear glass box of coloured balls, two red
// buttons for a face, on a yellow base. "Drop the claw": the claw goes down and always comes up
// with a ball (the draw is decided first, so it's fair); the ball pops open on what the play paid
// (WI-13): a collectible charm, an act key or a real prize. This file: the words, the 2D cabinet
// (Calm/Still, no WebGL) and the reveal (3D with full motion, ClawReveal3D; flat otherwise). The
// 3D cabinet on the Arcade set is cabinet3d.ts (stage chunk). Sounds: the picked claw whirr and
// drop (docs/listen/app-sounds.html), the synth if they can't play.
import { motion, useAnimate } from 'motion/react'
import { lazy, Suspense, useState } from 'react'
import { appSound } from '../../../audio/appSounds'
import { buzz } from '../../../audio/haptics'
import { revealFanfare, unwrapPop, unwrapWhoosh } from '../../../audio/synth'
import { springs } from '../../../motion/springs'
import { t } from '../../../strings/en'
import { capped, type FlatMachineProps, type RevealLook, type RevealProps, type RewardMachine } from '../types'

const ClawReveal3D = lazy(() => import('./ClawReveal3D'))

/** The cabinet's colours (the still): purple body, yellow base, red buttons. */
export const CLAW = { purple: '#8A5CE6', purpleD: '#5B34B0', yellow: '#FFC93C', red: '#E8343A', glass: '#E6F4FF' }
/** The balls in the glass, left to right. */
const BALLS = ['#FF4F4F', '#FFC93C', '#36D399', '#2F7BFF', '#9B6BFF', '#FF8AC0', '#FF4F4F', '#43C6DB', '#FFC93C', '#36D399']

/** The claw: a cable, a hub and three prongs (open 0 → closed 1). */
function ClawShape({ x, y, close = 0 }: { x: number; y: number; close?: number }) {
  const a = 28 - close * 20
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x="-3" y="-400" width="6" height="400" fill="#3C5068" />
      <rect x="-16" y="-6" width="32" height="16" rx="5" fill="#AFC3DA" stroke="#1B2A3A" strokeWidth="3" />
      {[-1, 0, 1].map((s) => (
        <path key={s} d={`M${s * 8},10 l${s * 6 + Math.sign(s) * (a / 3)},22 l${-s * 4},10`} fill="none" stroke="#1B2A3A" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </g>
  )
}

/** The 2D cabinet: the claw drops, grabs a ball, lifts it and lets it fall down the chute. */
function Claw({ title, locked, tier, label, onDropped }: FlatMachineProps) {
  const [scope, animate] = useAnimate()
  const [busy, setBusy] = useState(false)
  const [grab, setGrab] = useState(false)
  const still = tier === 'still'

  async function spend() {
    if (locked || busy) return
    setBusy(true)
    buzz('drop')
    claw.sounds.spend?.()
    if (!still) {
      await capped(async () => {
        await animate('.claw-arm', { y: [0, 112] }, { duration: 0.6, ease: 'easeIn' })
        setGrab(true)
        await animate('.claw-arm', { y: [112, 0] }, { duration: 0.5, ease: 'easeOut' })
        await animate('.claw-arm', { x: [0, -96] }, { duration: 0.4 })
      }, 2200)
    }
    buzz('crack')
    claw.sounds.open?.()
    setGrab(false)
    if (!still) void animate('.claw-arm', { x: 0 }, { duration: 0.3 })
    setBusy(false)
    onDropped()
  }

  return (
    <div ref={scope} className="gashapon claw-machine">
      <svg viewBox="0 0 380 440" role="img" aria-label={`${title} ${t.machines.claw.cabinet}${locked ? ', locked' : ''}`} onClick={() => void spend()}>
        <ellipse cx="190" cy="424" rx="150" ry="12" fill="#000" opacity=".25" />
        <g style={locked ? { filter: 'saturate(.4) brightness(.85)' } : undefined}>
          {/* The top: a purple crown with the name plate. */}
          <rect x="70" y="20" width="240" height="44" rx="14" fill={CLAW.purple} stroke="#1B2A3A" strokeWidth="4" />
          <rect x="120" y="30" width="140" height="24" rx="8" fill="#fff" stroke="#1B2A3A" strokeWidth="3" />
          <text x="190" y="48" textAnchor="middle" fontFamily="Fredoka" fontWeight="700" fontSize={title.length > 13 ? 12 : 15} fill="#1B2A3A">
            {title.toUpperCase()}
          </text>
          {/* The glass box with the balls and the claw. */}
          <rect x="80" y="64" width="220" height="170" fill={CLAW.glass} stroke="#1B2A3A" strokeWidth="4" />
          <clipPath id="claw-glass">
            <rect x="82" y="66" width="216" height="166" />
          </clipPath>
          <g clipPath="url(#claw-glass)">
            <g className="claw-arm">
              <ClawShape x={200} y={96} close={grab ? 1 : 0} />
              {grab ? <circle cx="200" cy="146" r="16" fill="#9B6BFF" stroke="#1B2A3A" strokeWidth="3" /> : null}
            </g>
            {BALLS.map((c, i) => (
              <circle key={i} cx={102 + (i % 5) * 40 + (i >= 5 ? 18 : 0)} cy={i >= 5 ? 198 : 216} r="17" fill={c} stroke="#1B2A3A" strokeWidth="3" />
            ))}
          </g>
          <rect x="80" y="64" width="16" height="170" fill="#fff" opacity=".35" />
          {/* The front: two red buttons for eyes, a little smile; the chute on the left. */}
          <rect x="70" y="234" width="240" height="104" rx="16" fill={CLAW.purple} stroke="#1B2A3A" strokeWidth="4" />
          <circle cx="160" cy="276" r="20" fill={CLAW.red} stroke="#1B2A3A" strokeWidth="4" />
          <circle cx="224" cy="276" r="20" fill={CLAW.red} stroke="#1B2A3A" strokeWidth="4" />
          <circle cx="166" cy="270" r="5" fill="#fff" opacity=".8" />
          <circle cx="230" cy="270" r="5" fill="#fff" opacity=".8" />
          <path d="M178,310 q14,12 28,0" fill="none" stroke="#1B2A3A" strokeWidth="4" strokeLinecap="round" />
          <rect x="92" y="296" width="40" height="30" rx="6" fill="#1B2A3A" />
          {/* The yellow base. */}
          <rect x="54" y="338" width="272" height="70" rx="16" fill={CLAW.yellow} stroke="#1B2A3A" strokeWidth="4" />
          <rect x="140" y="360" width="100" height="14" rx="7" fill="#E0A21A" />
        </g>
      </svg>
      <button className="btn y press twist-btn" disabled={locked || busy} onClick={() => void spend()} hidden={locked}>
        {label}
      </button>
    </div>
  )
}

/** The flat reveal (Calm/Still, or no WebGL): the claw holds the ball up; its halves pop apart on the reward. */
export function FlatBall({ look, still }: { look: RevealLook; still: boolean }) {
  return (
    <>
      <motion.svg className="prize-capsule claw-ball" viewBox="-10 -120 240 370" overflow="visible" aria-hidden initial={still ? false : { y: -80 }} animate={{ y: 0 }} transition={springs.slap}>
        <ClawShape x={110} y={-30} close={0.2} />
        <motion.path d="M14,110 a96,96 0 0 1 192,0z" fill={look.colour} stroke="#1B2A3A" strokeWidth="5" initial={still ? false : { y: 0 }} animate={{ y: -30, rotate: -10 }} transition={{ delay: still ? 0 : 0.3, ...springs.pop }} />
        <motion.path d="M14,124 a96,96 0 0 0 192,0z" fill={look.colour} stroke="#1B2A3A" strokeWidth="5" opacity=".9" initial={still ? false : { y: 0 }} animate={{ y: 22, rotate: 6 }} transition={{ delay: still ? 0 : 0.3, ...springs.pop }} />
      </motion.svg>
      <motion.span className={`prize-big${look.gold ? ' gold' : ''}`} aria-hidden initial={still ? false : { scale: 0 }} animate={{ scale: 1 }} transition={{ delay: still ? 0 : 0.45, ...springs.bounce }}>
        {look.art ? <img src={look.art} alt="" /> : look.kind === 'key' ? '🔑' : look.emoji}
      </motion.span>
    </>
  )
}

/** With full motion the ball is 3D (ClawReveal3D); in Calm/Still or without WebGL it's flat. */
function Reveal({ look, still, calm }: RevealProps) {
  const [flat, setFlat] = useState(calm)
  return flat ? (
    <FlatBall look={look} still={still} />
  ) : (
    <Suspense fallback={null}>
      <ClawReveal3D look={look} still={still} onFail={() => setFlat(true)} />
    </Suspense>
  )
}

export const claw: RewardMachine = {
  id: 'claw',
  words: t.machines.claw,
  Flat: Claw,
  Reveal,
  sounds: { spend: () => appSound('claw_whirr', unwrapWhoosh), open: () => appSound('claw_drop', unwrapPop), reveal: revealFanfare },
  haptics: { spend: 'drop', open: 'crack' },
}
